import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { Ticker, TickerLink } from '@benzinga/ticker-ui';
import { formatMillions, formatColumnDate, dateFilterParams, headerInfoTooltipDef } from '../lib/utils';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

const SizeFilter = [
  {
    label: '$0 to $100M',
    rules: (value: number) => {
      return value >= 0 && value <= 100000000;
    },
  },
  {
    label: '$100M to $300M',
    rules: (value: number) => {
      return value >= 100000000 && value <= 300000000;
    },
  },
  {
    label: '$300M to $500M',
    rules: (value: number) => {
      return value >= 300000000 && value <= 500000000;
    },
  },
  {
    label: '$500M to $1B',
    rules: (value: number) => {
      return value >= 250000000 && value <= 1000000000;
    },
  },
  {
    label: '$1B+',
    rules: (value: number) => {
      return value >= 1000000000;
    },
  },
];

export default [
  {
    cellClass: 'ticker-cell',
    field: 'date',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Announced',
    initialSort: 'desc' as ColDef['initialSort'],
    initialWidth: 110,
    minWidth: 100,
    valueFormatter: ({ data }) => formatColumnDate(data.date),
    valueGetter: ({ data }) => {
      if (!data.date) return null;

      return data.date;
    },
    ...headerInfoTooltipDef('Date Potential Deal is announced to the public'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'date_expected',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
    },
    headerName: 'Expected',
    initialWidth: 100,
    minWidth: 90,
    valueFormatter: ({ data }) => {
      if (!data.date_expected) return '-';
      return data.date_expected;
    },
    ...headerInfoTooltipDef('Date the deal is expected to close'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'date_completed',
    filter: 'agDateColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },
    headerName: 'Completed',
    initialWidth: 110,
    minWidth: 100,
    valueFormatter: ({ data }) => formatColumnDate(data.date_completed),
    valueGetter: ({ data }) => {
      if (!data.date_completed) return null;

      return data.date_completed;
    },
    ...headerInfoTooltipDef('Actual Date deal closed on'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: any) => {
      return (
        <div className="flex items-center flex-wrap">
          <span className="mr-1">{data.acquirer_name}</span>
          {data.acquirer_ticker && (
            <span className="flex items-center">
              (
              <Ticker
                symbol={data.acquirer_ticker}
                targetElement={<TickerLink symbol={data.acquirer_ticker}>{data.acquirer_ticker}</TickerLink>}
              />
              )
            </span>
          )}
        </div>
      );
    },
    cellRendererParams: {
      tickerDescriptionKey: 'acquirer_name',
      tickerSymbolKey: 'acquirer_ticker',
    },
    field: 'acquirer_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Acquirer Name',
    initialWidth: 240,
    minWidth: 240,
    ...headerInfoTooltipDef('Full name and symbol of company doing the acquisition'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ data }: any) => {
      return (
        <div className="flex items-center flex-wrap">
          <span className="mr-1">{data.target_name}</span>
          {data.target_ticker && (
            <span className="flex items-center">
              (
              <Ticker
                symbol={data.target_ticker}
                targetElement={<TickerLink symbol={data.target_ticker}>{data.target_ticker}</TickerLink>}
              />
              )
            </span>
          )}
        </div>
      );
    },
    cellRendererParams: {
      tickerDescriptionKey: 'target_name',
      tickerSymbolKey: 'target_ticker',
    },
    field: 'target_name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Target Name',
    initialWidth: 220,
    minWidth: 210,
    ...headerInfoTooltipDef('Full name and symbol of the company being acquired'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'deal_type',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Type',
    initialWidth: 100,
    minWidth: 90,
    ...headerInfoTooltipDef('Type of deal being proposed for acquisition'),
  },
  {
    field: 'deal_size',
    filter: 'priceFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
      values: SizeFilter,
    },
    headerName: 'Size',
    initialWidth: 85,
    minWidth: 80,
    valueFormatter: ({ value }) => formatMillions(value, true),
    ...headerInfoTooltipDef('Monetary value of the acquisition or merger'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'currency',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Currency',
    initialWidth: 90,
    minWidth: 80,
    ...headerInfoTooltipDef('Currency in which the deal is done in'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'deal_payment_type',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Payment Type',
    initialWidth: 130,
    minWidth: 120,
    ...headerInfoTooltipDef('The way the acquiring company plans on paying for the acquisition of the target company'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'deal_status',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Status',
    initialWidth: 100,
    minWidth: 90,
    ...headerInfoTooltipDef('Stage in which the deal is currently in'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 90,
    minWidth: 85,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
